'use client'
import { Link } from '@/i18n/routing'
import { useLocale } from 'next-intl'
import { getPostHref } from '@/utilities/getPostHref'
import { PayloadImage } from '@/components/Media/PayloadImage'
import { format } from 'date-fns'
import type { Post } from '@/payload-types'

interface FeaturedPostsProps {
  posts: Post[]
}

export function FeaturedPosts({ posts }: FeaturedPostsProps) {
  const locale = useLocale()

  if (posts.length === 0) return null

  const [mainPost, ...secondaryPosts] = posts

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* Main featured post */}
      <div className="lg:col-span-7">
        <Link
          href={getPostHref(mainPost, locale as 'en' | 'cs')}
          className="group block relative aspect-[16/9] rounded-2xl overflow-hidden bg-black/40 backdrop-blur-sm border border-white/10 hover:border-strandforge-purple/50 transition-all duration-300"
        >
          {mainPost.heroImage && typeof mainPost.heroImage === 'object' && (
            <PayloadImage
              src={mainPost.heroImage.url || ''}
              alt={mainPost.heroImage.alt || mainPost.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-110"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-strandforge-purple/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="absolute bottom-0 left-0 right-0 p-8">
            <h3 className="text-3xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-strandforge-purple-dark group-hover:via-strandforge-purple group-hover:to-strandforge-purple-light group-hover:bg-clip-text transition-all duration-300 mb-3 leading-tight">
              {mainPost.title}
            </h3>
            {mainPost.excerpt && (
              <p className="text-gray-300 text-base line-clamp-2 mb-4 leading-relaxed">
                {mainPost.excerpt}
              </p>
            )}
            {mainPost.publishedAt && (
              <time className="text-gray-400 text-sm font-medium">
                {format(new Date(mainPost.publishedAt), 'MMM dd, yyyy')}
              </time>
            )}
          </div>
        </Link>
      </div>

      {/* Secondary featured posts */}
      <div className="lg:col-span-5 space-y-8">
        {secondaryPosts.map((post) => (
          <Link
            key={post.id}
            href={getPostHref(post, locale as 'en' | 'cs')}
            className="group block relative aspect-[16/9] rounded-xl overflow-hidden bg-black/40 backdrop-blur-sm border border-white/10 hover:border-strandforge-purple/50 transition-all duration-300"
          >
            {post.heroImage && typeof post.heroImage === 'object' && (
              <PayloadImage
                src={post.heroImage.url || ''}
                alt={post.heroImage.alt || post.title}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-110"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-strandforge-purple/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="absolute bottom-0 left-0 right-0 p-6">
              {post.categories && post.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.categories.slice(0, 1).map((category) => (
                    <span
                      key={typeof category === 'object' ? category.id : category}
                      className="bg-strandforge-purple/30 backdrop-blur-sm border border-strandforge-purple/50 text-strandforge-purple-light text-xs font-medium px-2.5 py-1 rounded-full"
                    >
                      {typeof category === 'object' ? category.title : category}
                    </span>
                  ))}
                </div>
              )}
              <h3 className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-strandforge-purple-dark group-hover:via-strandforge-purple group-hover:to-strandforge-purple-light group-hover:bg-clip-text transition-all duration-300 mb-2 leading-tight">
                {post.title}
              </h3>
              {post.publishedAt && (
                <time className="text-gray-400 text-sm font-medium">
                  {format(new Date(post.publishedAt), 'MMM dd, yyyy')}
                </time>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
